/**
 * Browser-camera-adapter (getUserMedia) met rVFC/rAF-frame loop,
 * meetbare verwerking en overlay-rendering van veldgrenzen.
 */

import type { CameraAdapter, CameraFrameStats, CameraInfo, FieldCalibration, FieldPoint } from "./types";
import { applyHomography, computeHomography, invertHomography } from "./homography";
import { PerformanceTracker } from "./perf";

export class BrowserCameraAdapter implements CameraAdapter {
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;

  async list(): Promise<CameraInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((d) => d.kind === "videoinput")
        .map((d, i) => {
          const label = d.label || `Camera ${i + 1}`;
          return {
            deviceId: d.deviceId,
            label,
            facingEnvironment: guessFacing(label),
          };
        });
    } catch {
      return [];
    }
  }

  async start(deviceId?: string): Promise<{ stream: MediaStream; video: HTMLVideoElement }> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("getUserMedia niet beschikbaar; HTTPS vereist voor camera");
    }
    const constraints: MediaStreamConstraints = {
      video: deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: { ideal: "environment" } },
      audio: false,
    };
    this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.video = document.createElement("video");
    this.video.playsInline = true;
    this.video.muted = true;
    this.video.srcObject = this.stream;
    void this.video.play().catch(() => {
      /* autoplay kan geblokkeerd zijn; de caller retryt na een gesture */
    });
    await this.waitForReady(this.video);
    return { stream: this.stream, video: this.video };
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.video) this.video.srcObject = null;
    this.stream = null;
    this.video = null;
  }

  isActive(): boolean {
    return !!this.stream && this.stream.active;
  }

  private waitForReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve) => {
      if (video.readyState >= 2 && video.videoWidth > 0) return resolve();
      const on = () => {
        video.removeEventListener("loadeddata", on);
        resolve();
      };
      video.addEventListener("loadeddata", on);
      setTimeout(on, 3000);
    });
  }
}

function guessFacing(label: string): boolean | null {
  const l = label.toLowerCase();
  if (/(back|rear)/.test(l)) return true;
  if (/(front|webcam|face)/.test(l)) return false;
  return null;
}

/* ------------------------------------------------------------------ */
/* Frame loop (rVFC waar beschikbaar, anders begrensde rAF)            */
/* ------------------------------------------------------------------ */

export interface FrameHandler {
  (now: number, dtMs: number, frame: HTMLVideoElement): void;
}

export class FrameLoop {
  private stopped = false;
  private rafId = 0;
  private useRvfc = true;
  private tracker = new PerformanceTracker();
  private onFrame: FrameHandler | null = null;
  private lastNow = 0;

  get stats(): CameraFrameStats {
    return this.tracker.snapshot();
  }

  start(video: HTMLVideoElement, onFrame: FrameHandler, opts: { useVideoFrameCallback: boolean; targetFps: number }): void {
    this.stop();
    this.onFrame = onFrame;
    this.stopped = false;
    this.useRvfc = opts.useVideoFrameCallback && typeof (video as unknown as { requestVideoFrameCallback?: unknown }).requestVideoFrameCallback === "function";
    if (this.useRvfc) {
      const v = video as unknown as {
        requestVideoFrameCallback: (cb: (now: number, meta: { expectedDisplayTime: number; presentedFrames: number }) => void) => number;
      };
      const loop = (now: number, _meta: { expectedDisplayTime: number; presentedFrames: number }): void => {
        if (this.stopped) return;
        const t0 = performance.now();
        this.onFrame?.(now, now - this.lastNow, video);
        this.lastNow = now;
        this.tracker.frame(now, performance.now() - t0);
        v.requestVideoFrameCallback(loop);
      };
      v.requestVideoFrameCallback(loop);
    } else {
      let interval = 1000 / Math.min(opts.targetFps, 30);
      const loop = (now: number): void => {
        if (this.stopped) return;
        const t0 = performance.now();
        this.onFrame?.(now, now - this.lastNow, video);
        this.lastNow = now;
        this.tracker.frame(now, performance.now() - t0);
        this.rafId = setTimeout(() => requestAnimationFrame(loop), Math.max(0, interval - (performance.now() - t0)));
      };
      this.rafId = requestAnimationFrame(loop);
    }
  }

  stop(): void {
    this.stopped = true;
    cancelAnimationFrame(this.rafId);
  }

  resetStats(): void {
    this.tracker.reset();
  }

  get usingVideoFrameCallback(): boolean {
    return this.useRvfc;
  }
}

/* ------------------------------------------------------------------ */
/* Overlay: veldlijnen, net, detectiezones op de video                  */
/* ------------------------------------------------------------------ */

export function drawCourtOverlay(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  cal: FieldCalibration | null,
  opts: { showZones: boolean; teamSides: Record<"left" | "right", string> },
): void {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (w === 0 || h === 0) return;
  ctx.clearRect(0, 0, w, h);
  if (!cal) return;

  const Hinv = invertHomography(cal.H);
  if (!Hinv) return;
  const toImg = (x: number, y: number): FieldPoint => applyHomography(Hinv, { x, y });

  const pw = cal.widthM;
  const ph = cal.heightM;

  // buitenrand
  strokeQuad(ctx, toImg, [
    [0, 0], [pw, 0], [pw, ph], [0, ph],
  ], "rgba(255,255,255,0.9)", 3);

  // netlijn (horizontaal midden in court-space)
  strokeQuad(ctx, toImg, [
    [0, ph / 2], [pw, ph / 2],
  ], "rgba(0,255,255,0.9)", 4);

  // middenlijn van het veld (verticaal, van net naar achterlijn aan weerszijden)
  strokeQuad(ctx, toImg, [
    [pw / 2, 0], [pw / 2, ph / 2],
  ], "rgba(0,255,255,0.6)", 2);

  // fysieke speelhelften (links/rechts van de camera)
  const leftTeam = opts.teamSides["left"];
  const rightTeam = opts.teamSides["right"];
  labelQuad(ctx, toImg, [[0, 0], [pw / 2, 0], [pw / 2, ph / 2], [0, ph / 2]], leftTeam, "left");
  labelQuad(ctx, toImg, [[pw / 2, 0], [pw, 0], [pw, ph / 2], [pw / 2, ph / 2]], rightTeam, "right");

  if (opts.showZones) {
    // duidelijk gemarkeerde "in-veld" zone (middelste 60% van het veld)
    const z = 0.2;
    ctx.fillStyle = "rgba(80,255,120,0.10)";
    fillQuad(ctx, toImg, [[pw * z, ph * z], [pw * (1 - z), ph * z], [pw * (1 - z), ph * (1 - z)], [pw * z, ph * (1 - z)]]);
  }
}

function strokeQuad(ctx: CanvasRenderingContext2D, toImg: (x: number, y: number) => FieldPoint, pts: number[][], color: string, width: number): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  pts.forEach(([x, y], i) => {
    const p = toImg(x, y);
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();
}

function fillQuad(ctx: CanvasRenderingContext2D, toImg: (x: number, y: number) => FieldPoint, pts: number[][]): void {
  ctx.beginPath();
  pts.forEach(([x, y], i) => {
    const p = toImg(x, y);
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
  ctx.fill();
}

function labelQuad(ctx: CanvasRenderingContext2D, toImg: (x: number, y: number) => FieldPoint, pts: number[][], label: string, side: string): void {
  const c = pts.reduce((acc, [x, y]) => {
    const p = toImg(x, y);
    return { x: acc.x + p.x / pts.length, y: acc.y + p.y / pts.length };
  }, { x: 0, y: 0 });
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "bold 24px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(`[${side}] ${label}`, c.x, c.y);
}

/** Resize helper: schaal een videoframe naar process-breedte. */
export function drawFrameToCanvas(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  processWidth: number,
): { width: number; height: number } | null {
  if (video.videoWidth === 0) return null;
  const scale = processWidth / video.videoWidth;
  const w = processWidth;
  const h = Math.max(2, Math.round(video.videoHeight * scale));
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const c = canvas.getContext("2d", { willReadFrequently: true });
  if (!c) return null;
  c.drawImage(video, 0, 0, w, h);
  return { width: w, height: h };
}

export function calibrationFromCorners(corners: FieldPoint[], widthM: number, heightM: number): FieldCalibration | null {
  // corners volgorde: TL, TR, BR, BL (beeld) -> court (0,0),(w,0),(w,h),(0,h)
  const dst: FieldPoint[] = [
    { x: 0, y: 0 },
    { x: widthM, y: 0 },
    { x: widthM, y: heightM },
    { x: 0, y: heightM },
  ];
  const H = computeHomography(corners, dst);
  if (!H) return null;
  return {
    H,
    cornersPx: corners.map((c) => [c.x, c.y]),
    teamsOnSides: { left: "A", right: "B" },
    widthM,
    heightM,
    createdAt: Date.now(),
  };
}
